import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Form, Flex, Select } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { parsePhoneNumber } from 'libphonenumber-js';
import type { CustomizationComponent } from '@/pages/Logiblocks/types';
import {
  inboundEventTypeList,
  trackingLinkTypeList,
} from '@/common/static_data';
import CheckboxMultiSelect from '@/components/CheckboxMultiSelect';
import { CustomSelect } from '@/components';
import useToken from '@/common/helper/useToken';
import {
  GetCompanyPhoneNumbers,
  type CompanyPhoneNumberOption,
} from '@/apis/settings';
import styles from '../NodeCustomization.module.css';
import '@/pages/Settings/AiAgents/AgentsTable.css';
import 'react-phone-input-2/lib/style.css';

function phoneOptionLabel(
  phoneNumber: string,
  label: string | null
): React.ReactNode {
  let flagClass = 'us';
  let display = phoneNumber;
  try {
    const parsed = parsePhoneNumber(phoneNumber);
    flagClass = (parsed.country ?? 'US').toLowerCase();
    display = `+${parsed.countryCallingCode} ${parsed.formatNational()}`;
  } catch {
    // fall through to raw number
  }
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span
        className='react-tel-input'
        style={{ width: 'auto', flexShrink: 0, lineHeight: 0 }}
      >
        <span
          className={`flag ${flagClass}`}
          style={{ display: 'inline-block' }}
        />
      </span>
      <span>{label ? `${display} (${label})` : display}</span>
    </span>
  );
}

export const InboundTriggerCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isReadOnly = false,
  isSidePannelOpen,
}) => {
  const [form] = Form.useForm();
  const { getToken } = useToken();
  const token = getToken();
  const [phoneOptions, setPhoneOptions] = useState<CompanyPhoneNumberOption[]>(
    []
  );
  const [phoneLoading, setPhoneLoading] = useState(false);
  const hasPatchedRef = useRef(false);

  const inboundEventType = nodeData.inbound_trigger?.inbound_event_type ?? '';
  const phoneNumber = nodeData.inbound_trigger?.phone_number ?? '';
  const trackingLinkTypes = useMemo(
    () => nodeData.inbound_trigger?.tracking_link_types ?? [],
    [nodeData.inbound_trigger?.tracking_link_types]
  );

  useEffect(() => {
    if (!token) return;
    setPhoneLoading(true);
    GetCompanyPhoneNumbers(token, 'InboundTrigger')
      .then((res) => {
        setPhoneOptions(res?.data?.data?.data ?? []);
      })
      .catch(() => setPhoneOptions([]))
      .finally(() => setPhoneLoading(false));
  }, [token]);

  useEffect(() => {
    if (!isSidePannelOpen || hasPatchedRef.current) return;
    if (!nodeData.inbound_trigger) return;
    hasPatchedRef.current = true;
    form.setFieldsValue({
      inboundEventType:
        nodeData.inbound_trigger.inbound_event_type || undefined,
      phoneNumber: nodeData.inbound_trigger.phone_number || undefined,
    });
  }, [isSidePannelOpen, nodeData, form]);

  useEffect(() => {
    if (!isSidePannelOpen) {
      hasPatchedRef.current = false;
    }
  }, [isSidePannelOpen]);

  const handleEventTypeChange = useCallback(
    (value: string) => {
      form.setFieldValue('phoneNumber', undefined);
      onChange({
        inbound_trigger: {
          inbound_event_type: value,
          phone_number: '',
          tracking_link_types: [],
        },
      });
    },
    [onChange, form]
  );

  const handlePhoneChange = useCallback(
    (value: string) => {
      onChange({
        inbound_trigger: {
          inbound_event_type: inboundEventType,
          phone_number: value,
          tracking_link_types: trackingLinkTypes,
        },
      });
    },
    [onChange, inboundEventType, trackingLinkTypes]
  );

  const handleTrackingLinkTypesChange = useCallback(
    (values: Array<string | number>) => {
      onChange({
        inbound_trigger: {
          inbound_event_type: inboundEventType,
          phone_number: '',
          tracking_link_types: values.map(String),
        },
      });
    },
    [onChange, inboundEventType]
  );

  return (
    <Form form={form} layout='vertical'>
      <Flex vertical gap={16}>
        <Form.Item name='inboundEventType' label='Inbound event type'>
          <CustomSelect
            width='100%'
            suffixIcon={
              <DownOutlined style={{ color: 'var(--text-secondary)' }} />
            }
            placeholder='Select...'
            options={inboundEventTypeList}
            bgColor='var(--light-gray)'
            textColor='var(--text-secondary)'
            borderRadius='8px'
            height='36px'
            border='none'
            onChange={handleEventTypeChange}
            disabled={isReadOnly}
            allowClear
          />
        </Form.Item>

        {inboundEventType === 'call' && (
          <div className={styles.customizationSection}>
            <label className={styles.sectionLabel}>Phone Number</label>
            <Form.Item name='phoneNumber' noStyle>
              <Select
                className='agentSelect agentSelect--darkText'
                value={phoneNumber || undefined}
                loading={phoneLoading}
                disabled={isReadOnly}
                placeholder='Select an inbound number'
                notFoundContent='No inbound numbers available'
                options={phoneOptions.map((n) => ({
                  value: n.phone_number,
                  label: phoneOptionLabel(n.phone_number, n.label),
                }))}
                onChange={handlePhoneChange}
              />
            </Form.Item>
          </div>
        )}

        {inboundEventType === 'tracking_link' && (
          <div className={styles.customizationSection}>
            <label className={styles.sectionLabel}>Type</label>
            <CheckboxMultiSelect
              titleText='Select type(s)'
              options={trackingLinkTypeList}
              value={trackingLinkTypes}
              onChange={handleTrackingLinkTypesChange}
              className='agentSelect agentSelect--darkText'
              disabled={isReadOnly}
              showTagCloseIcon={true}
              background='#5E5E5E'
              color='#fff'
            />
          </div>
        )}
      </Flex>
    </Form>
  );
};
